if (!customElements.get('media-gallery')) {
  const MEDIA_IMAGE_FALLBACK_MESSAGE = '画像を読み込めませんでした';

  const getPublicMediaAlt = (alt) => {
    const normalizedAlt = (alt || '').trim();
    const hasInternalMarker = normalizedAlt.includes('[ASTALIE-ASSET:') || normalizedAlt.includes('[ERP ');
    if (!hasInternalMarker) return normalizedAlt;

    return normalizedAlt
      .split(' / ')
      .map((part) => part.trim())
      .find((part) => part && !part.includes('[ASTALIE-ASSET:') && !part.includes('[ERP ')) || '商品画像';
  };

  const showMediaImageFallback = (image) => {
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.classList.remove('product-media-image--retrying');
    image.classList.add('product-media-image--failed');

    const host = image.parentElement;
    if (!host || host.querySelector(':scope > .product-media-image-fallback')) return;

    host.classList.add('product-media-image-fallback-host');
    const fallback = document.createElement('span');
    fallback.className = 'product-media-image-fallback';
    fallback.setAttribute('role', 'img');
    fallback.setAttribute('aria-label', MEDIA_IMAGE_FALLBACK_MESSAGE);
    fallback.textContent = MEDIA_IMAGE_FALLBACK_MESSAGE;
    image.insertAdjacentElement('afterend', fallback);
  };

  const retryMediaImage = (image) => {
    const publicAlt = getPublicMediaAlt(image.getAttribute('alt'));
    image.alt = '';

    if (image.dataset.mediaFallbackAttempted === 'true') {
      showMediaImageFallback(image);
      return;
    }

    const fallbackSrc = image.getAttribute('src');
    image.dataset.mediaFallbackAttempted = 'true';
    image.classList.add('product-media-image--retrying');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');

    if (!fallbackSrc) {
      showMediaImageFallback(image);
      return;
    }

    const retryImage = new Image();
    retryImage.onload = () => {
      if (!image.isConnected) return;
      image.alt = publicAlt;
      image.removeAttribute('aria-hidden');
      image.classList.remove('product-media-image--retrying');
      image.src = fallbackSrc;
    };
    retryImage.onerror = () => {
      if (image.isConnected) showMediaImageFallback(image);
    };
    retryImage.src = fallbackSrc;
  };

  document.addEventListener('error', (event) => {
    const image = event.target;
    if (image?.tagName !== 'IMG' || !image.closest('media-gallery, product-modal')) return;
    retryMediaImage(image);
  }, true);

  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.initializeImageFallbacks();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    initializeImageFallbacks() {
      this.querySelectorAll('img').forEach((image) => {
        if (image.complete && image.naturalWidth === 0) retryMediaImage(image);
      });
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!prepend && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!activeThumbnail) return;

      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
